import { useEffect, useState, useRef, useCallback } from "react";

interface UseDynamicTableHeightOptions {
  /** Estimated row height in pixels */
  estimatedRowHeight?: number;
  /** Minimum number of rows to show */
  minRows?: number;
  /** Maximum number of rows to show */
  maxRows?: number;
}

export const useDynamicTableHeight = (
  options: UseDynamicTableHeightOptions = {},
) => {
  const {
    estimatedRowHeight = 52, // Default row height
    minRows = 5,
    maxRows = 100,
  } = options;

  const [pageSize, setPageSize] = useState(minRows);
  const [tableHeight, setTableHeight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const calculateOptimalPageSize = useCallback(() => {
    if (!containerRef.current) return minRows;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();

    // More precise calculation: use viewport height minus current position and minimal bottom padding
    const viewportHeight = window.innerHeight;
    const bottomPadding = 40; // Minimal space for pagination controls
    const availableHeight = viewportHeight - containerRect.top - bottomPadding;

    // Account for search bar and pagination controls within the container
    const searchBarHeight = 80; // Search input + padding
    const paginationHeight = 60; // Pagination controls + padding
    const tableContentHeight =
      availableHeight - searchBarHeight - paginationHeight;

    // Calculate how many rows can fit
    const rowsCanFit = Math.floor(tableContentHeight / estimatedRowHeight);

    // Ensure we stay within bounds
    const optimalRows = Math.max(minRows, Math.min(maxRows, rowsCanFit));

    setTableHeight(tableContentHeight);
    return optimalRows;
  }, [estimatedRowHeight, minRows, maxRows]);

  const updatePageSize = useCallback(() => {
    const newPageSize = calculateOptimalPageSize();
    setPageSize(newPageSize);
  }, [calculateOptimalPageSize]);

  useEffect(() => {
    // Initial calculation
    updatePageSize();

    // Update on window resize
    const handleResize = () => {
      updatePageSize();
    };

    // Update when container size changes (using ResizeObserver if available)
    let resizeObserver: ResizeObserver | null = null;
    if (containerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        updatePageSize();
      });
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [updatePageSize]);

  return {
    pageSize,
    tableHeight,
    containerRef,
    updatePageSize,
  };
};
